import * as React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  Check,
  ChevronDown,
  KeyRound,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Sun,
  X,
} from "lucide-react";
import { cn, initials, relativeTime } from "@/lib/utils";
import { api } from "@/lib/api";
import { canAny } from "@/lib/permissions";
import { ROLE_LABELS } from "@/lib/permissions";
import { useAuthStore } from "@/stores/authStore";
import { useThemeStore, type Theme } from "@/stores/themeStore";
import { useRealtimeStore } from "@/lib/realtime";
import { NAV } from "./nav";
import { Button } from "@/components/ui/button";
import { Badge, Separator } from "@/components/ui/primitives";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Hint,
  Popover,
  PopoverContent,
  PopoverTrigger,
  TooltipProvider,
} from "@/components/ui/overlays";

// ── Sidebar ─────────────────────────────────────────────────────────────

const SidebarNav = ({ onNavigate }: { onNavigate?: () => void }) => {
  const permissions = useAuthStore((s) => s.permissions);
  const location = useLocation();

  // Sidebar badges reuse the dashboard KPI payload rather than adding another
  // endpoint. Refetched on the same interval the dashboard uses.
  const { data: counts } = useQuery({
    queryKey: ["dashboard", "kpis"],
    queryFn: () => api.get<Record<string, number>>("/dashboard/kpis"),
    enabled: canAny(permissions, ["dashboard:view"]),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return (
    <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4" aria-label="Main">
      {NAV.map((section) => {
        const visible = section.items.filter((item) =>
          canAny(permissions, item.permissions),
        );
        if (visible.length === 0) return null;

        return (
          <div key={section.label}>
            <p className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
              {section.label}
            </p>
            <ul className="space-y-0.5">
              {visible.map((item) => {
                const active = item.matchPrefix
                  ? location.pathname.startsWith(item.to)
                  : location.pathname === item.to;
                const count = item.countKey ? counts?.[item.countKey] : undefined;

                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={onNavigate}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "group flex items-center gap-2.5 rounded-md px-2 py-2 text-sm transition-colors",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        active
                          ? "bg-primary/12 font-medium text-primary"
                          : "text-muted-foreground hover:bg-surface hover:text-foreground",
                      )}
                    >
                      <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="flex-1 truncate">{item.label}</span>
                      {count != null && count > 0 && (
                        <span
                          className={cn(
                            "numeric rounded-full px-1.5 py-0.5 text-[10px] font-semibold",
                            active
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground group-hover:bg-foreground/10",
                          )}
                        >
                          {count > 99 ? "99+" : count}
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
};

const Brand = () => (
  <Link
    to="/"
    className="flex items-center gap-2.5 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <span className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-surface text-base">
      🐾
    </span>
    <span className="flex flex-col leading-none">
      <span className="text-sm font-semibold">GetPet</span>
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
        Admin
      </span>
    </span>
  </Link>
);

// ── Theme toggle ────────────────────────────────────────────────────────

const ThemeToggle = () => {
  const { theme, resolved, setTheme, toggle } = useThemeStore();

  const options: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: "dark", label: "Dark", icon: Moon },
    { value: "light", label: "Light", icon: Sun },
    { value: "system", label: "System", icon: Monitor },
  ];

  return (
    <DropdownMenu>
      <Hint label={`Theme: ${theme}`}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            // Plain click flips dark/light; the menu is for choosing "system".
            onDoubleClick={toggle}
            aria-label={`Change theme, currently ${theme}`}
          >
            {resolved === "dark" ? <Moon aria-hidden /> : <Sun aria-hidden />}
          </Button>
        </DropdownMenuTrigger>
      </Hint>
      <DropdownMenuContent>
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {options.map((opt) => (
          <DropdownMenuItem key={opt.value} onClick={() => setTheme(opt.value)}>
            <opt.icon aria-hidden />
            <span className="flex-1">{opt.label}</span>
            {theme === opt.value && <Check className="h-3.5 w-3.5" aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── Notification bell ───────────────────────────────────────────────────

const severityDot: Record<string, string> = {
  info: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
};

const NotificationBell = () => {
  const { notifications, unreadCount, markAllRead, markRead, clear } =
    useRealtimeStore();
  const [open, setOpen] = React.useState(false);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next && unreadCount > 0) markAllRead();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
        >
          <Bell aria-hidden />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[9px] font-bold text-destructive-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold">Notifications</p>
          </div>
          {notifications.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clear} className="h-6 px-2 text-xs">
              Clear
            </Button>
          )}
        </div>
        <Separator />

        <div
          className="max-h-96 overflow-y-auto"
          role="log"
          aria-live="polite"
          aria-label="Activity feed"
        >
          {notifications.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              Nothing yet. New signups, reports, and tickets appear here as they
              happen.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {notifications.map((n) => {
                const body = (
                  <>
                    <span
                      className={cn(
                        "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
                        severityDot[n.severity],
                      )}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {n.title}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {n.detail}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-muted-foreground/70">
                        {relativeTime(n.ts)}
                      </span>
                    </span>
                  </>
                );

                return (
                  <li key={n.id} className={cn(!n.read && "bg-primary/[0.04]")}>
                    {n.href ? (
                      <Link
                        to={n.href}
                        onClick={() => {
                          markRead(n.id);
                          setOpen(false);
                        }}
                        className="flex gap-2 px-3 py-2.5 transition-colors hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {body}
                      </Link>
                    ) : (
                      <div className="flex gap-2 px-3 py-2.5">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Account menu ────────────────────────────────────────────────────────

const AccountMenu = () => {
  const { admin, roleKey, logout } = useAuthStore();
  if (!admin) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 pl-1.5 pr-2">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
            {initials(admin.name)}
          </span>
          <span className="hidden max-w-[10rem] truncate text-sm sm:inline">
            {admin.name}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-60">
        <div className="px-2 py-2">
          <p className="truncate text-sm font-medium">{admin.name}</p>
          <p className="truncate text-xs text-muted-foreground">{admin.email}</p>
          <Badge variant="secondary" className="mt-1.5">
            {ROLE_LABELS[roleKey ?? ""] ?? roleKey}
          </Badge>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <KeyRound aria-hidden />
            Security &amp; password
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onClick={() => void logout()}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ── Shell ───────────────────────────────────────────────────────────────

export const AppShell = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  // Close the drawer on navigation.
  React.useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex min-h-screen bg-background">
        {/* Skip link, first in tab order. */}
        <a
          href="#main"
          className="sr-only-focusable fixed left-3 top-3 z-[60] rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
        >
          Skip to content
        </a>

        {/* Desktop sidebar */}
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-border bg-card lg:flex">
          <Brand />
          <Separator />
          <SidebarNav />
        </aside>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-black/60"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
            />
            <div className="absolute inset-y-0 left-0 flex w-64 flex-col border-r border-border bg-card">
              <div className="flex items-center justify-between">
                <Brand />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="mr-2"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close navigation"
                >
                  <X aria-hidden />
                </Button>
              </div>
              <Separator />
              <SidebarNav onNavigate={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-card/95 px-4 backdrop-blur">
            <Button
              variant="ghost"
              size="icon-sm"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu aria-hidden />
            </Button>

            <div className="flex-1" />

            <NotificationBell />
            <ThemeToggle />
            <Separator orientation="vertical" className="mx-1 h-6" />
            <AccountMenu />
          </header>

          <main id="main" className="min-w-0 flex-1 px-4 py-5 sm:px-6">
            <Outlet />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
};
